import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Divider,
  Select,
  FormControl,
  List,
  ListItem,
  Checkbox,
  InputLabel,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ShareIcon from '@mui/icons-material/Share';
import HomeIcon from '@mui/icons-material/Home';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublicIcon from '@mui/icons-material/Public';
import { userFilesAPI } from '../api';
import { buildPublicFolderShareUrl, copyTextToClipboard } from '../utils/publicShareUrl';
import {
  describeSkippedMoves,
  isFileDragEvent,
  moveDraggedFilesToFolder,
} from '../utils/fileListView';

// Build a map of parentId -> children[]
function buildTree(folders) {
  const byParent = new Map();
  for (const f of folders) {
    const key = f.parentId || null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(f);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }
  return byParent;
}

// Collect descendant ids (including self) for a folder
function descendantIds(folders, rootId) {
  const out = new Set([rootId]);
  const byParent = new Map();
  for (const f of folders) {
    const key = f.parentId || null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(f);
  }
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop();
    const children = byParent.get(id) || [];
    for (const c of children) {
      out.add(c.id);
      stack.push(c.id);
    }
  }
  return out;
}

function NameDialog({ open, title, initial = '', onClose, onSubmit, confirmLabel = 'Save' }) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setName(initial);
  }, [open, initial]);
  const submit = async () => {
    const t = name.trim();
    if (!t) return;
    setBusy(true);
    try {
      await onSubmit(t);
      onClose();
    } catch (e) {
      alert(e?.response?.data?.error || 'Operation failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Folder name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          sx={{ mt: 1 }}
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !name.trim()}>
          {busy ? <CircularProgress size={18} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MoveDialog({ open, folder, folders, onClose, onMove }) {
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null);
  const forbidden = useMemo(
    () => (folder ? descendantIds(folders, folder.id) : new Set()),
    [folder, folders]
  );

  useEffect(() => {
    if (open) setTarget(null);
  }, [open]);

  const tree = useMemo(() => buildTree(folders), [folders]);

  const submit = async () => {
    if (!folder) return;
    setBusy(true);
    try {
      await onMove(folder.id, target);
      onClose();
    } catch (e) {
      alert(e?.response?.data?.error || 'Move failed');
    } finally {
      setBusy(false);
    }
  };

  const renderNode = (node, depth) => {
    const disabled = forbidden.has(node.id);
    const isSelected = target === node.id;
    const children = tree.get(node.id) || [];
    return (
      <Box key={node.id}>
        <Box
          onClick={() => !disabled && setTarget(node.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: 1 + depth * 2,
            pr: 1,
            py: 0.5,
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: disabled ? 'text.disabled' : 'inherit',
            bgcolor: isSelected ? 'action.selected' : 'transparent',
            '&:hover': disabled ? undefined : { bgcolor: 'action.hover' },
          }}
        >
          <FolderIcon fontSize="small" />
          <Typography variant="body2" noWrap>{node.name}</Typography>
        </Box>
        {children.map((c) => renderNode(c, depth + 1))}
      </Box>
    );
  };

  const roots = tree.get(null) || [];

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Move "{folder?.name}"</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          onClick={() => setTarget(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 0.75,
            cursor: 'pointer',
            bgcolor: target === null ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <HomeIcon fontSize="small" />
          <Typography variant="body2">Root (top level)</Typography>
        </Box>
        <Divider />
        {roots.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No other folders. Move to root or cancel.
            </Typography>
          </Box>
        ) : (
          roots.map((r) => renderNode(r, 0))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || (folder && target === (folder.parentId || null))}
        >
          {busy ? <CircularProgress size={18} /> : 'Move here'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getContactUsers(users, currentUserId, currentUser) {
  const currentUserTeamIds = currentUser?.teamIds || [];
  const currentUserContactIds = currentUser?.contactIds || [];
  return users.filter((user) => {
    if (user.id === currentUserId) return false;
    const userTeamIds = user.teamIds || [];
    const userContactIds = user.contactIds || [];
    if (currentUserContactIds.includes(user.id) || userContactIds.includes(currentUserId)) {
      return true;
    }
    if (currentUserTeamIds.length > 0 && userTeamIds.length > 0) {
      return userTeamIds.some((teamId) => currentUserTeamIds.includes(teamId));
    }
    return false;
  }).sort((a, b) => a.username.localeCompare(b.username));
}

function PublicShareTab({ folder, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!folder) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCopied(false);
    userFilesAPI.getFolderPublicShare(folder.id)
      .then((res) => {
        if (cancelled) return;
        setEnabled(!!res.data?.enabled);
        setShareToken(res.data?.shareToken || null);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load public sharing settings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [folder]);

  const handleToggle = async (nextEnabled) => {
    if (!nextEnabled && !window.confirm(
      'Turn off public sharing? The current link will stop working permanently.'
    )) {
      return;
    }
    setToggling(true);
    setError(null);
    setCopied(false);
    try {
      const res = await userFilesAPI.setFolderPublicShare(folder.id, nextEnabled);
      setEnabled(!!res.data?.enabled);
      setShareToken(res.data?.shareToken || null);
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update public sharing.');
    } finally {
      setToggling(false);
    }
  };

  const shareUrl = shareToken
    ? buildPublicFolderShareUrl(window.location.origin, shareToken)
    : '';

  const handleCopy = async () => {
    if (!shareUrl) return;
    const ok = await copyTextToClipboard(shareUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setError('Could not copy automatically — select the link and copy it manually.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={toggling}
            onChange={(e) => handleToggle(e.target.checked)}
          />
        }
        label="Share publicly"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Anyone with the link can browse and download this folder and its subfolders
        without logging in. They cannot upload, rename or delete anything.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {enabled && shareUrl && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Public link"
            value={shareUrl}
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
            >
              Copy link
            </Button>
            {copied && (
              <Typography variant="body2" color="success.main">Copied!</Typography>
            )}
          </Box>
        </>
      )}
      {toggling && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
}

function ShareFolderDialog({ open, folder, users, currentUserId, currentUser, onClose, onSave, onPublicShareChanged }) {
  const [readSelected, setReadSelected] = useState(new Set());
  const [writeSelected, setWriteSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState(0);

  const contacts = useMemo(
    () => getContactUsers(users, currentUserId, currentUser),
    [users, currentUserId, currentUser]
  );

  useEffect(() => {
    if (!open || !folder) return;
    setQuery('');
    setTab(0);
    setLoading(true);
    userFilesAPI.getFolderSharing(folder.id)
      .then((res) => {
        const writeIds = new Set(res.data?.sharedWithWrite || []);
        const readIds = new Set(res.data?.sharedWith || []);
        writeIds.forEach((id) => readIds.add(id));
        setReadSelected(readIds);
        setWriteSelected(writeIds);
      })
      .catch(() => {
        setReadSelected(new Set());
        setWriteSelected(new Set());
      })
      .finally(() => setLoading(false));
  }, [open, folder]);

  const q = query.trim().toLowerCase();
  const filtered = contacts.filter((u) => !q || u.username.toLowerCase().includes(q));

  const toggleRead = (userId) => {
    const isOn = readSelected.has(userId);
    if (isOn) {
      setReadSelected((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      setWriteSelected((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    } else {
      setReadSelected((prev) => new Set(prev).add(userId));
    }
  };

  const toggleWrite = (userId) => {
    const isOn = writeSelected.has(userId);
    if (isOn) {
      setWriteSelected((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    } else {
      setWriteSelected((prev) => new Set(prev).add(userId));
      setReadSelected((prev) => new Set(prev).add(userId));
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const writeIds = Array.from(writeSelected);
      const readIds = Array.from(new Set([...readSelected, ...writeIds]));
      await onSave(folder.id, readIds, writeIds);
      onClose();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to update sharing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>Share &quot;{folder?.name}&quot;</DialogTitle>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 2 }}>
        <Tab label="Contacts" />
        <Tab label="Public link" />
      </Tabs>
      <DialogContent dividers sx={{ p: 0 }}>
        {tab === 0 ? (
          <>
            <Box sx={{ p: 2, pb: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search contacts..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </Box>
            {loading ? (
              <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No contacts found</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    Contact
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 56, textAlign: 'center' }}>
                    Read
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 56, textAlign: 'center' }}>
                    Write
                  </Typography>
                </Box>
                <List dense disablePadding>
                  {filtered.map((u) => (
                    <ListItem key={u.id} disablePadding sx={{ px: 1 }}>
                      <ListItemText primary={u.username} sx={{ pl: 1, minWidth: 0 }} />
                      <Checkbox
                        checked={readSelected.has(u.id)}
                        onChange={() => toggleRead(u.id)}
                        sx={{ width: 56 }}
                        inputProps={{ 'aria-label': `Read access for ${u.username}` }}
                      />
                      <Checkbox
                        checked={writeSelected.has(u.id)}
                        onChange={() => toggleWrite(u.id)}
                        sx={{ width: 56 }}
                        inputProps={{ 'aria-label': `Write access for ${u.username}` }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        ) : (
          <PublicShareTab folder={folder} onChanged={onPublicShareChanged} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {tab === 0 ? 'Cancel' : 'Close'}
        </Button>
        {tab === 0 && (
          <Button variant="contained" onClick={submit} disabled={saving || loading}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function FoldersTree({
  folders,
  loading,
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
  users = [],
  currentUserId,
  currentUser,
  folderViewSource = 'mine',
  onFolderViewChange,
  sharedContacts = [],
  onUpdateFolderSharing,
  onRefreshFolders,
  onFilesMoved,
  dense = true,
}) {
  const isOwnView = folderViewSource === 'mine';
  const viewLabel = isOwnView
    ? 'My Folders'
    : sharedContacts.find((c) => c.id === folderViewSource)?.username || 'Shared folders';
  const tree = useMemo(() => buildTree(folders), [folders]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFolder, setMenuFolder] = useState(null); // null means root
  const [createUnder, setCreateUnder] = useState(undefined); // undefined=closed; null=root parent; string=folder id
  const [renameFolder, setRenameFolder] = useState(null);
  const [moveFolder, setMoveFolder] = useState(null);
  const [shareFolder, setShareFolder] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const hoverExpandTimer = useRef(null);

  useEffect(() => {
    const clearDropState = () => {
      if (hoverExpandTimer.current) {
        clearTimeout(hoverExpandTimer.current);
        hoverExpandTimer.current = null;
      }
      setDropTargetId(null);
    };
    window.addEventListener('dragend', clearDropState);
    return () => window.removeEventListener('dragend', clearDropState);
  }, []);

  // Auto-expand ancestors of selected folder so the selection stays visible in the tree.
  useEffect(() => {
    if (!selectedFolderId) return;
    const byId = new Map(folders.map((f) => [f.id, f]));
    setExpanded((prev) => {
      const next = new Set(prev);
      let cur = byId.get(selectedFolderId);
      while (cur && cur.parentId) {
        next.add(cur.parentId);
        cur = byId.get(cur.parentId);
      }
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [selectedFolderId, folders]);

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openMenu = (e, folder) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuFolder(folder);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuFolder(null);
  };

  const handleCreateHere = () => {
    setCreateUnder(menuFolder ? menuFolder.id : null);
    closeMenu();
  };
  const handleRenameClick = () => {
    setRenameFolder(menuFolder);
    closeMenu();
  };
  const handleMoveClick = () => {
    setMoveFolder(menuFolder);
    closeMenu();
  };
  const handleShareClick = () => {
    setShareFolder(menuFolder);
    closeMenu();
  };
  const handleDeleteClick = async () => {
    const f = menuFolder;
    closeMenu();
    if (!f) return;
    if (!window.confirm(`Delete "${f.name}" and all its contents?`)) return;
    try {
      await onDeleteFolder(f.id);
    } catch (e) {
      alert(e?.response?.data?.error || 'Delete failed');
    }
  };

  const handleFolderDragOver = (e, node) => {
    if (!isFileDragEvent(e)) return;
    const canDrop = node.readOnly === false && node.id !== selectedFolderId;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = canDrop ? 'move' : 'none';
    if (dropTargetId !== node.id) {
      if (hoverExpandTimer.current) {
        clearTimeout(hoverExpandTimer.current);
        hoverExpandTimer.current = null;
      }
      setDropTargetId(node.id);
    }
    const children = tree.get(node.id) || [];
    if (children.length > 0 && !expanded.has(node.id) && !hoverExpandTimer.current) {
      const id = node.id;
      hoverExpandTimer.current = setTimeout(() => {
        setExpanded((prev) => new Set(prev).add(id));
        hoverExpandTimer.current = null;
      }, 450);
    }
  };

  const handleFolderDragLeave = (e, node) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (hoverExpandTimer.current) {
      clearTimeout(hoverExpandTimer.current);
      hoverExpandTimer.current = null;
    }
    setDropTargetId((cur) => (cur === node.id ? null : cur));
  };

  const handleFolderDrop = async (e, node) => {
    setDropTargetId(null);
    if (hoverExpandTimer.current) {
      clearTimeout(hoverExpandTimer.current);
      hoverExpandTimer.current = null;
    }
    if (node.readOnly !== false || node.id === selectedFolderId) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    try {
      const result = await moveDraggedFilesToFolder({
        event: e,
        targetFolderId: node.id,
        moveFiles: userFilesAPI.moveFiles,
      });
      if (!result) return;
      const message = describeSkippedMoves(result.skipped);
      if (message) alert(message);
      if (result.moved?.length) onFilesMoved?.();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to move files.');
    }
  };

  const renderNode = (node, depth) => {
    const children = tree.get(node.id) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(node.id);
    const isSelected = selectedFolderId === node.id;
    const isReadOnly = node.readOnly || !isOwnView;
    return (
      <Box key={node.id}>
        <Box
          onClick={() => onSelectFolder?.(node.id)}
          onDragOver={(e) => handleFolderDragOver(e, node)}
          onDragLeave={(e) => handleFolderDragLeave(e, node)}
          onDrop={(e) => handleFolderDrop(e, node)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: 1 + depth * 3,
            pr: 0.5,
            py: dense ? 0.25 : 0.5,
            cursor: 'pointer',
            bgcolor: dropTargetId === node.id && node.readOnly === false && node.id !== selectedFolderId
              ? 'action.selected'
              : isSelected ? 'action.selected' : 'transparent',
            outline: dropTargetId === node.id && node.readOnly === false && node.id !== selectedFolderId
              ? '2px solid'
              : 'none',
            outlineColor: 'primary.main',
            outlineOffset: -2,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) toggle(node.id);
            }}
            sx={{
              width: 28,
              height: 28,
              visibility: hasChildren ? 'visible' : 'hidden',
            }}
            aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
          >
            {isExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
          {isSelected || isExpanded ? (
            <FolderOpenIcon
              fontSize="small"
              sx={{ mx: 0.5, color: isSelected ? 'primary.main' : 'inherit' }}
            />
          ) : (
            <FolderIcon fontSize="small" sx={{ mx: 0.5, color: 'inherit' }} />
          )}
          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: 1,
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? 'primary.main' : 'inherit',
            }}
          >
            {node.name}
          </Typography>
          {isOwnView && node.publicShareEnabled && (
            <Tooltip title="Shared with a public link">
              <PublicIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
            </Tooltip>
          )}
          {isOwnView && !isReadOnly && (
            <IconButton
              size="small"
              onClick={(e) => openMenu(e, node)}
              sx={{ width: 22, height: 22 }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {isExpanded && children.map((c) => renderNode(c, depth + 1))}
      </Box>
    );
  };

  const roots = tree.get(null) || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <FormControl size="small" fullWidth>
          <InputLabel id="folder-view-label">View</InputLabel>
          <Select
            labelId="folder-view-label"
            label="View"
            value={folderViewSource}
            onChange={(e) => onFolderViewChange?.(e.target.value)}
          >
            <MenuItem value="mine">My Folders</MenuItem>
            {sharedContacts.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.username}&apos;s folders
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }} noWrap>
            {viewLabel}
          </Typography>
          {isOwnView && (
            <Tooltip title="New folder (root)">
              <IconButton size="small" onClick={() => setCreateUnder(null)}>
                <CreateNewFolderIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', py: 0.5 }}>
        {loading && folders.length === 0 ? (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={22} />
          </Box>
        ) : roots.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {isOwnView ? 'No folders yet.' : 'No folders shared with you.'}
            </Typography>
            {isOwnView && (
              <Button
                size="small"
                variant="contained"
                startIcon={<CreateNewFolderIcon />}
                onClick={() => setCreateUnder(null)}
              >
                New folder
              </Button>
            )}
          </Box>
        ) : (
          roots.map((r) => renderNode(r, 0))
        )}
      </Box>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem onClick={handleCreateHere}>
          <ListItemIcon>
            <CreateNewFolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New subfolder</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRenameClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMoveClick}>
          <ListItemIcon>
            <DriveFileMoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleShareClick}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <NameDialog
        open={createUnder !== undefined}
        title={createUnder ? 'New subfolder' : 'New folder'}
        initial=""
        confirmLabel="Create"
        onClose={() => setCreateUnder(undefined)}
        onSubmit={async (name) => {
          await onCreateFolder(name, createUnder || null);
          if (createUnder) {
            setExpanded((prev) => new Set(prev).add(createUnder));
          }
        }}
      />

      <NameDialog
        open={!!renameFolder}
        title="Rename folder"
        initial={renameFolder?.name || ''}
        confirmLabel="Save"
        onClose={() => setRenameFolder(null)}
        onSubmit={async (name) => {
          await onRenameFolder(renameFolder.id, name);
        }}
      />

      <MoveDialog
        open={!!moveFolder}
        folder={moveFolder}
        folders={folders}
        onClose={() => setMoveFolder(null)}
        onMove={onMoveFolder}
      />

      <ShareFolderDialog
        open={!!shareFolder}
        folder={shareFolder}
        users={users}
        currentUserId={currentUserId}
        currentUser={currentUser}
        onClose={() => setShareFolder(null)}
        onSave={onUpdateFolderSharing}
        onPublicShareChanged={onRefreshFolders}
      />
    </Box>
  );
}
